<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <section class="provider-tariff-shell">
      <template v-if="currTab.title == 'View All Items' || currTab.title == 'AI Marker'">
        <div class="provider-tariff-scroll-marker" ref="tariffShelf"></div>
        <section class="provider-tariff-card">
          <div class="provider-tariff-intro">
            <div>
              <label class="active teal-text">Tariff Shelf</label>
              <h6>{{ schedule || "Provider tariff schedule" }}</h6>
              <p>
                {{ currTab.title == "AI Marker" ? "Mark agreed primary care items for future auto-vetting." : "Review prices, add items, or replace tariff records." }}
              </p>
            </div>
            <div class="provider-tariff-context">
              <span>{{ scheme || "Scheme not loaded" }}</span>
              <span>{{ lastUpdatedLabel }}</span>
            </div>
          </div>

          <section class="provider-tariff-search-card">
            <div>
              <label class="active teal-text">Search Tariff</label>
              <h6>Find a procedure or service</h6>
            </div>
            <form class="provider-tariff-search" @submit.prevent>
              <i class="material-icons">search</i>
              <input type="search" v-model="searchItem" placeholder="Search tariff item" />
              <button type="button" @click.prevent="handleClearSearch" v-if="searchItem" title="Clear search">
                <i class="material-icons">close</i>
              </button>
            </form>
          </section>

          <div class="progress provider-tariff-progress" v-if="isLoading">
            <div class="indeterminate"></div>
          </div>

          <section class="provider-tariff-list" v-if="tariffs.length">
            <div class="provider-tariff-head" :class="{ 'is-ai-marker': currTab.title == 'AI Marker' }">
              <span>#</span>
              <span>{{ currTab.title == "AI Marker" ? "Tariff Item / Price" : "Tariff Item" }}</span>
              <span v-if="currTab.title != 'AI Marker'">Price</span>
              <span>{{ currTab.title == "AI Marker" ? "AI Marker" : "Action" }}</span>
            </div>

            <div
              class="provider-tariff-row"
              :class="{ 'is-editing': tariff.id == isEditing, 'is-ai-marker': currTab.title == 'AI Marker' }"
              v-for="(tariff, index) in tariffs"
              :key="tariff.id"
            >
              <template v-if="currTab.title == 'AI Marker'">
                <span class="tariff-index">{{ numberBadge(index) }}</span>
                <div class="tariff-item">
                  <strong>{{ tariff.item }}</strong>
                  <small>
                    <span class="tariff-inline-price">&#8358;{{ money(tariff.price) }}</span>
                    <span>{{ tariffActiveDate(tariff.created_at) }}</span>
                  </small>
                </div>
                <div class="switch provider-ai-marker-switch">
                  <label>
                    Off
                    <input
                      type="checkbox"
                      :checked="isTariffAutoVettable(tariff)"
                      :disabled="autoVetUpdating == tariff.id"
                      @change="handleAutoVetToggle(tariff, $event)"
                    />
                    <span class="lever"></span>
                    On
                  </label>
                </div>
              </template>

              <template v-else-if="tariff.id != isEditing">
                <span class="tariff-index">{{ numberBadge(index) }}</span>
                <div class="tariff-item">
                  <strong>{{ tariff.item }}</strong>
                  <small>{{ tariffActiveDate(tariff.created_at) }}</small>
                </div>
                <div class="tariff-price">
                  <span>&#8358;{{ money(tariff.price) }}</span>
                </div>
                <button type="button" class="tariff-edit-action" @click.prevent="handleEdit(tariff)" title="Edit tariff">
                  <i class="material-icons">edit</i>
                </button>
              </template>

              <form class="provider-tariff-edit-form" @submit.prevent="handleEditConfirm(tariff)" v-else>
                <span class="tariff-index">{{ numberBadge(index) }}</span>
                <div class="provider-tariff-edit-item">
                  <label class="active teal-text">Tariff Item</label>
                  <strong>{{ item }}</strong>
                </div>
                <div class="input-field">
                  <input type="text" v-model="price" />
                  <label class="active teal-text">Price</label>
                </div>
                <div class="tariff-edit-actions">
                  <button type="button" class="btn-flat tariff-cancel-action" @click.prevent="handleCancel()">
                    Cancel
                  </button>
                  <button class="btn tariff-update-action" :class="{ disabled: isSubmitting }">
                    Update
                  </button>
                </div>
              </form>
            </div>
          </section>

          <section class="provider-tariff-empty" v-if="!tariffs.length && !isLoading">
            <i class="material-icons">inventory_2</i>
            <h6>No tariff item found</h6>
            <p>Try another search term or add a new tariff item for this provider.</p>
          </section>
        </section>

        <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-if="tariffs.length" />
      </template>

      <AddSingleTariff :provider="provider" @update="handleUpdate" v-if="currTab.title == 'Add Item'" />

      <AddTariff
        :providerId="provider.id"
        :planId="planId"
        @reload="handleReload"
        v-if="currTab.title == 'Replace All'"
      />

      <TariffBand
        :providerId="provider.id"
        :planId="planId"
        @reload="handleReload"
        v-if="currTab.title == 'Tariff Band'"
      />
    </section>
  </template>
</template>

<script>
import { onMounted, ref, computed, watchEffect } from "vue";
import getItemsOfItems from "@/composables/getItemsOfItems";
import getItemsOfItemsOfItems from "@/composables/getItemsOfItemsOfItems";
import postItem from "@/composables/postItem";
import putItem from "@/composables/putItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import AddTariff from "@/components/tariff/AddTariffComponent.vue";
import TariffBand from "@/components/tariff/TariffBandComponent.vue";
import AddSingleTariff from "@/components/tariff/AddSingleTariffComponent.vue";
import M from "materialize-css";

export default {
  name: "TariffProviderComponent",
  components: { LoadingDiv, PaginationDiv, AddTariff, TariffBand, AddSingleTariff },
  props: ["provider", "startTab"],
  emits: ["reload"],
  setup(props, context) {
    const response = ref(null);
    const planId = ref(null);
    const schedule = ref(null);
    const scheme = ref(null);
    const updated = ref(null);
    const error = ref(null);
    const isEditing = ref(null);
    const isSubmitting = ref(null);
    const autoVetUpdating = ref(null);
    const isReady = ref(false);
    const isLoading = ref(true);
    const currUrl = ref(null);
    const currHook = ref(null);
    const item = ref(null);
    const price = ref(null);
    const searchItem = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const tariffShelf = ref(null);

    // Title
    watchEffect(() => {
      if (response.value) {
        // load title
        setTimeout(() => {
          var elems = document.querySelectorAll(".tooltipped");
          M.Tooltip.init(elems, {});
        }, 2000);
      }
    });

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    const money = (value) => {
      return Number(value || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const formatUpdatedDate = (value) => {
      if (!value) {
        return null;
      }

      const safeValue = typeof value === "string" ? value.replace(" ", "T") : value;
      const date = new Date(safeValue);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date.toLocaleString("en", {
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const lastUpdatedLabel = computed(() => {
      const date = formatUpdatedDate(updated.value);

      return date ? `Updated ${date}` : "Update date unavailable";
    });

    const tariffActiveDate = (value) => {
      if (!value) {
        return "Active date unavailable";
      }

      const safeValue = typeof value === "string" ? value.replace(" ", "T") : value;
      const date = new Date(safeValue);

      if (Number.isNaN(date.getTime())) {
        return "Active date unavailable";
      }

      return `Active since ${date.toLocaleDateString("en", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })}`;
    };

    const isTariffAutoVettable = (tariff) => {
      return tariff?.is_auto_vettable === true || tariff?.is_auto_vettable === 1 || tariff?.is_auto_vettable === "1";
    };

    // get function
    const getTariffsFromApi = (table, id1, id2) => {
      (async () => {
        const { res, err } = await getItemsOfItemsOfItems(table, id1, id2);

        if (res.value) {

          // filter zero cost from the list
          // res.value.data.data = res.value.data.data.filter((x) => Number(x.price) != 0);

          response.value = res.value.data;

        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, title: "View All Items", icon: null, status: null });
      tabs.value.push({
        id: 2,
        title: "Replace All",
        icon: "edit",
        status: "remove",
      });
      tabs.value.push({
        id: 3,
        title: "Add Item",
        icon: "add_circle",
        status: "add",
      });
      tabs.value.push({
        id: 4,
        title: "Tariff Band",
        icon: "sync",
        status: "add",
      });
      tabs.value.push({
        id: 5,
        title: "AI Marker",
        icon: "psychology",
        status: "add",
      });

      // set default (from Props)
      currTab.value = tabs.value.find(x => x.title.toLowerCase().includes(props.startTab));
    });

    // set schedule
    const scheduleReload = () => {
      (async () => {
        const { res } = await getItemsOfItems("schedule", props.provider.schedule_id);
        if (res.value) {
          // search
          schedule.value = res.value.data.plan.name;
          planId.value = res.value.data.plan.id;
          scheme.value = res.value.data.plan.scheme.name;
          updated.value = res.value.data.updated_at;
        }

        isReady.value = true;
      })();
    }

    // set tariff
    const tariffRestart = () => {
      // set current Url
      currUrl.value = "tariff/schedule";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getTariffsFromApi(currUrl.value, props.provider.schedule_id, props.provider.id); // provider.schedule_id && provider.id
    };

    // starter
    onMounted(() => {
      if (props.startTab === "view" || props.startTab === "ai") {
        // load schedule
        scheduleReload();
        // load tariff base
        tariffRestart();
      } else {
        // load schedule only
        scheduleReload();
      }
    });

    const handleUpdate = () => {
      // load schedule
      scheduleReload();
      // load tariff base
      tariffRestart();
    };

    const handleReload = (val) => {
      if (val) {
        // tariff update
        context.emit("reload", true);
      }
    };

    // handle edit
    const handleEdit = (tariff) => {
      isEditing.value = tariff.id;
      item.value = tariff.item;
      price.value = tariff.price;
    };

    // handle cancel
    const handleCancel = () => {
      isEditing.value = null;
      item.value = null;
      price.value = null;
    };

    // handle edit confirm
    const handleEditConfirm = async (tariff) => {
      // put all together
      let newVal = {
        item: item.value,
        price: price.value,
        schedule_id: props.provider.schedule_id,
        scope: props.provider.id,
      };

      // post item
      const { res, err } = await postItem("tariff/add", newVal);

      if (res.value) {
        // success, update current array
        response.value.data.forEach((x) => {
          if (x.id == tariff.id) {
            x.item = item.value;
            x.price = price.value;
            x.created_at = res.value.data.created_at || x.created_at;
            x.is_auto_vettable = res.value.data.is_auto_vettable ?? x.is_auto_vettable;
          }
        });
      } else {
        error.value = `Error: ${item.value} not updated! (${err.value})`;
      }

      handleCancel();
    };

    const handleAutoVetToggle = async (tariff, event) => {
      if (autoVetUpdating.value) {
        event.target.checked = isTariffAutoVettable(tariff);
        return;
      }

      const nextValue = event.target.checked;
      autoVetUpdating.value = tariff.id;

      const { res, err } = await putItem(`tariff/${tariff.id}`, "auto-vettable", {
        is_auto_vettable: nextValue,
        provider_id: props.provider.id,
      });

      if (res.value) {
        const updatedTariff = res.value.data;

        response.value.data = response.value.data.map((x) => {
          if (x.id == tariff.id) {
            return { ...x, ...updatedTariff };
          }

          return x;
        });

        M.toast({
          html: nextValue ? "AI Marker enabled" : "AI Marker disabled",
          classes: "teal",
        });
      } else {
        event.target.checked = !nextValue;
        error.value = err.value || "Unable to update AI marker.";
        M.toast({ html: "AI Marker not updated", classes: "red" });
      }

      autoVetUpdating.value = null;
    };

    // restart
    const handleClearSearch = () => {
      // start laoding
      isLoading.value = true;
      // null search
      searchItem.value = null;
      // load tariff base
      tariffRestart();
    };

    // switch page
    const handlePage = (page) => {
      tariffShelf.value?.scrollIntoView({ behavior: "smooth", block: "start" });
      // start laoding
      isLoading.value = true;
      // get new page items from function
      getTariffsFromApi(
        currUrl.value,
        props.provider.schedule_id,
        `${props.provider.id}${currHook.value}page=${page}`
      );
    };

    // handle curr status
    const handleActive = (tab) => {
      // update show and components
      currTab.value = tab;
    };

    // search watcher
    watchEffect(() => {
      if (searchItem.value) {
        // start laoding
        isLoading.value = true;
        // set current Url
        currUrl.value = "tariff/schedule/search";
        // set current Hook
        currHook.value = `?q=${searchItem.value}&`;
        // get searchItem items from function
        getTariffsFromApi(
          currUrl.value,
          props.provider.schedule_id,
          `${props.provider.id}?q=${searchItem.value}`
        );
      } else {
        handleClearSearch();
      }
    });

    // output results
    const tariffs = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    return {
      handleEdit,
      handleActive,
      handleEditConfirm,
      handleCancel,
      handleClearSearch,
      handlePage,
      handleUpdate,
      handleReload,
      handleAutoVetToggle,
      numberBadge,
      money,
      tariffActiveDate,
      isTariffAutoVettable,
      error,
      isEditing,
      isSubmitting,
      autoVetUpdating,
      isReady,
      isLoading,
      searchItem,
      item,
      price,
      currTab,
      tabs,
      planId,
      schedule,
      scheme,
      updated,
      lastUpdatedLabel,
      tariffs,
      allPages,
      tariffShelf,
    };
  },
};
</script>

<style scoped>
.provider-tariff-shell {
  color: #263238;
  display: grid;
  gap: 14px;
}

.provider-tariff-card {
  background: #ffffff;
  border: 1px solid #e3eaed;
  border-radius: 6px;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  overflow: hidden;
}

.provider-tariff-scroll-marker {
  height: 1px;
  scroll-margin-top: 76px;
}

.provider-tariff-intro,
.provider-tariff-search-card,
.provider-tariff-list,
.provider-tariff-empty {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.provider-tariff-intro {
  align-items: center;
  border-bottom: 1px solid #eef3f5;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 18px 20px;
}

.provider-tariff-intro label,
.provider-tariff-search-card label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.provider-tariff-intro h6,
.provider-tariff-search-card h6,
.provider-tariff-empty h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
}

.provider-tariff-intro p,
.provider-tariff-empty p {
  color: #607d8b;
  line-height: 1.55;
  margin: 7px 0 0;
}

.provider-tariff-context {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.provider-tariff-context span {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 999px;
  color: #455a64;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 11px;
}

.provider-tariff-search-card {
  align-items: center;
  border-bottom: 1px solid #eef3f5;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(180px, 0.8fr) minmax(260px, 1.4fr);
  padding: 18px 20px;
}

.provider-tariff-search {
  align-items: center;
  border: 1px solid #b7c5ca;
  border-radius: 6px;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 48px;
  padding: 0 12px;
}

.provider-tariff-search i {
  color: #00897b;
  font-size: 21px;
}

.provider-tariff-search input {
  border: 0 !important;
  box-shadow: none !important;
  color: #263238;
  font-size: 1rem;
  height: 44px;
  margin: 0;
}

.provider-tariff-search button {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  padding: 0;
  width: 30px;
}

.provider-tariff-progress {
  margin: -4px 0 0;
}

.provider-tariff-list {
  padding-bottom: 14px;
  overflow: hidden;
}

.provider-tariff-head,
.provider-tariff-row {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-template-columns: 56px minmax(220px, 1.6fr) minmax(140px, 0.7fr) 72px;
}

.provider-tariff-head.is-ai-marker,
.provider-tariff-row.is-ai-marker {
  grid-template-columns: 56px minmax(220px, 1fr) minmax(160px, 0.28fr);
}

.provider-tariff-head {
  background: rgba(255, 255, 255, 0.72);
  border-bottom: 1px solid #eeeeee;
  color: #607d8b;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 13px 20px;
  text-transform: uppercase;
}

.provider-tariff-row {
  border-bottom: 1px solid #eeeeee;
  min-height: 82px;
  padding: 18px 20px;
}

.provider-tariff-row:last-child {
  border-bottom: 0;
}

.provider-tariff-row.is-editing {
  background: #fffdf5;
}

.tariff-index {
  color: #607d8b;
  font-weight: 700;
}

.tariff-item strong {
  color: #263238;
  display: block;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
}

.tariff-item small {
  align-items: center;
  color: #607d8b;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.84rem;
  gap: 7px;
  margin-top: 4px;
}

.tariff-inline-price {
  color: #33691e;
  font-weight: 800;
}

.tariff-price span {
  background: #f1f8e9;
  border-radius: 6px;
  color: #33691e;
  display: inline-flex;
  font-weight: 800;
  min-width: 128px;
  padding: 10px 12px;
}

.tariff-edit-action {
  align-items: center;
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  color: #00897b;
  cursor: pointer;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  padding: 0;
  width: 38px;
}

.tariff-edit-action i {
  font-size: 19px;
}

.provider-ai-marker-switch {
  align-items: center;
  display: flex;
  justify-content: flex-end;
}

.provider-ai-marker-switch label {
  align-items: center;
  color: #607d8b;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  gap: 8px;
  white-space: nowrap;
}

.provider-ai-marker-switch .lever {
  margin: 0;
}

.provider-ai-marker-switch input[type="checkbox"]:checked + .lever {
  background-color: #9bd2cb;
}

.provider-ai-marker-switch input[type="checkbox"]:checked + .lever::after {
  background-color: #00897b;
}

.provider-tariff-edit-form {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-column: 1 / -1;
  grid-template-columns: 56px minmax(220px, 1.4fr) minmax(140px, 0.6fr) auto;
}

.provider-tariff-edit-form .input-field {
  margin: 0;
}

.provider-tariff-edit-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.provider-tariff-edit-item label {
  line-height: 1;
}

.provider-tariff-edit-item strong {
  color: #263238;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.35;
}

.provider-tariff-edit-form input {
  border-bottom-color: #b0bec5;
  color: #263238;
  font-weight: 700;
  margin: 8px 0 0;
}

.tariff-edit-actions {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.tariff-cancel-action {
  color: #455a64;
  font-weight: 700;
  text-transform: none;
}

.tariff-update-action {
  background: #00897b;
  border-radius: 6px;
  box-shadow: none;
  font-weight: 700;
  text-transform: none;
}

.provider-tariff-empty {
  padding: 32px 20px;
  text-align: center;
}

.provider-tariff-empty i {
  color: #90a4ae;
  font-size: 32px;
}

@media only screen and (max-width: 900px) {
  .provider-tariff-intro,
  .provider-tariff-search-card {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }

  .provider-tariff-intro {
    flex-direction: column;
  }

  .provider-tariff-context {
    align-items: flex-start;
  }

  .provider-tariff-head {
    display: none;
  }

  .provider-tariff-row,
  .provider-tariff-edit-form {
    align-items: flex-start;
    grid-template-columns: 42px 1fr;
  }

  .tariff-price,
  .tariff-edit-action,
  .provider-ai-marker-switch,
  .provider-tariff-edit-item,
  .provider-tariff-edit-form .input-field,
  .tariff-edit-actions {
    grid-column: 2;
  }

  .provider-tariff-row.is-ai-marker {
    grid-template-columns: 42px 1fr;
  }

  .tariff-edit-actions {
    justify-content: flex-start;
  }
}
</style>
